/**
 * Utility functions for handling calculator paths in the admin panel
 */

import { AdminSession, AdminReport } from '../types/admin.types';

export type SessionMode = 'TAS_ONLY' | 'ROI_ANALYSIS' | 'UNKNOWN';

export interface PathInfo {
  mode: SessionMode;
  label: string;
  shortLabel: string;
  color: string;
  bgColor: string;
  description: string;
  isRoosterBranded: boolean;
}

/**
 * Infer calculator path from session data when calculatorPath is missing
 */
function inferCalculatorPath(session: AdminSession): string {
  // Check if session has ROI-related data (savings)
  const hasROIData = !!(
    session.totalAnnualSavings || 
    session.year1Savings || 
    session.steadyStateSavings ||
    (session.results?.savings?.totalAnnualSavings)
  );
  
  if (hasROIData) {
    // Session has ROI data, likely from ROI calculator
    return '/';
  }
  
  // Check if it's a Rooster assessment (default TAS path for Rooster)
  if (session.isRoosterAssessment) {
    return '/rooster-tas';
  }
  
  // Default fallback based on whether it has any TAS data
  if (session.currentTAS || session.projectedTAS) {
    return '/rooster-tas'; // Most common TAS path
  }
  
  // Final fallback to ROI mode
  return '/';
}

/**
 * Convert a calculator path to a display label and metadata
 */
export function getPathInfo(calculatorPath?: string): PathInfo;
export function getPathInfo(session: AdminSession): PathInfo;
export function getPathInfo(pathOrSession?: string | AdminSession): PathInfo {
  let calculatorPath: string | undefined;
  
  // Handle overloaded function - determine if we got a path string or session object
  if (typeof pathOrSession === 'string') {
    calculatorPath = pathOrSession;
  } else if (pathOrSession && typeof pathOrSession === 'object') {
    // It's a session object
    calculatorPath = pathOrSession.calculatorPath;
    // If calculatorPath is missing/invalid, infer it from session data
    if (!calculatorPath || calculatorPath === 'undefined' || calculatorPath === 'null') {
      calculatorPath = inferCalculatorPath(pathOrSession);
    }
  } else {
    calculatorPath = pathOrSession;
  }
  // Handle missing, empty, or "undefined" string paths
  if (!calculatorPath || calculatorPath === 'undefined' || calculatorPath === 'null') {
    return {
      mode: 'UNKNOWN',
      label: 'Unknown',
      shortLabel: 'Unknown',
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      description: 'Path information not available',
      isRoosterBranded: false
    };
  }

  // Normalize the path
  const normalizedPath = calculatorPath.toLowerCase();

  if (normalizedPath === '/tas' || normalizedPath === '/generic') {
    return {
      mode: 'TAS_ONLY',
      label: 'TAS Only',
      shortLabel: 'TAS',
      color: 'text-blue-700',
      bgColor: 'bg-blue-50 border-blue-200',
      description: 'TAS assessment without ROI analysis (Generic)',
      isRoosterBranded: false
    };
  }

  if (normalizedPath === '/rooster-tas') {
    return {
      mode: 'TAS_ONLY',
      label: 'TAS Only (Rooster)',
      shortLabel: 'TAS',
      color: 'text-blue-700',
      bgColor: 'bg-blue-50 border-blue-200',
      description: 'TAS assessment without ROI analysis (Rooster branded)',
      isRoosterBranded: true
    };
  }

  if (normalizedPath === '/' || normalizedPath === '/roi' || normalizedPath === '/admin') {
    return {
      mode: 'ROI_ANALYSIS',
      label: 'ROI Analysis',
      shortLabel: 'ROI',
      color: 'text-green-700',
      bgColor: 'bg-green-50 border-green-200',
      description: 'Complete TAS assessment with ROI analysis',
      isRoosterBranded: true
    };
  }


  // Fallback for unknown paths
  return {
    mode: 'UNKNOWN',
    label: `Custom (${calculatorPath})`,
    shortLabel: 'Custom',
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    description: `Unknown calculator path: ${calculatorPath}`,
    isRoosterBranded: false
  };
}

/**
 * Get all available path modes for filtering
 */
export function getAllPathModes(): Array<{ value: SessionMode; label: string; color: string }> {
  return [
    { value: 'TAS_ONLY', label: 'TAS Only', color: 'text-blue-700' },
    { value: 'ROI_ANALYSIS', label: 'ROI Analysis', color: 'text-green-700' },
    { value: 'UNKNOWN', label: 'Unknown', color: 'text-gray-600' }
  ];
}

/**
 * Check if a path represents a TAS-only mode
 */
export function isTasOnlyMode(calculatorPath?: string): boolean;
export function isTasOnlyMode(session: AdminSession): boolean;
export function isTasOnlyMode(pathOrSession?: string | AdminSession): boolean {
  const pathInfo = getPathInfo(pathOrSession as any);
  return pathInfo.mode === 'TAS_ONLY';
}

/**
 * Check if a path represents an ROI analysis mode
 */
export function isRoiAnalysisMode(calculatorPath?: string): boolean;
export function isRoiAnalysisMode(session: AdminSession): boolean;
export function isRoiAnalysisMode(pathOrSession?: string | AdminSession): boolean {
  const pathInfo = getPathInfo(pathOrSession as any);
  return pathInfo.mode === 'ROI_ANALYSIS';
}

/**
 * Check if a path represents a Rooster-branded experience
 */
export function isRoosterBrandedPath(calculatorPath?: string): boolean {
  const pathInfo = getPathInfo(calculatorPath);
  return pathInfo.isRoosterBranded;
}

/**
 * Get the correct report URL for a session or report based on its calculator mode
 */
export function getReportUrl(session: AdminSession): string;
export function getReportUrl(report: AdminReport): string;
export function getReportUrl(sessionOrReport: AdminSession | AdminReport): string {
  const reportId = sessionOrReport.reportId;
  if (!reportId) {
    return '#';
  }
  
  // Check if it's an AdminReport (has totalAnnualSavings property)
  if ('totalAnnualSavings' in sessionOrReport) {
    // AdminReport - determine type based on whether it has ROI data
    const hasROIData = (sessionOrReport.totalAnnualSavings || 0) > 0;
    return hasROIData ? `/report/${reportId}` : `/tas-report/${reportId}`;
  }
  
  // AdminSession - Check for hasROIAnalysis first, with fallback logic
  const session = sessionOrReport as AdminSession;
  
  // First check if hasROIAnalysis is explicitly set (new reports)
  if ('hasROIAnalysis' in session && typeof session.hasROIAnalysis === 'boolean') {
    return session.hasROIAnalysis ? `/report/${reportId}` : `/tas-report/${reportId}`;
  }
  
  // Fallback: Check for ROI data indicators for existing reports
  const hasROIData = !!(
    session.totalAnnualSavings || 
    session.year1Savings || 
    session.steadyStateSavings ||
    (session.results?.savings?.totalAnnualSavings)
  );
  
  if (hasROIData) {
    return `/report/${reportId}`;
  }
  
  // Final fallback: use path-based detection
  if (isTasOnlyMode(session)) {
    return `/tas-report/${reportId}`;
  }
  
  return `/report/${reportId}`;
}

/**
 * Get the correct report URL for an original report ID in an edit session
 * Since we only have the ID, we'll determine the URL based on the current session's mode
 */
export function getOriginalReportUrl(session: AdminSession): string {
  if (!session.originalReportId) {
    return '#';
  }
  
  // Use the same logic as the current session - if current session is TAS-only, 
  // assume original was also TAS-only
  if (isTasOnlyMode(session)) {
    return `/tas-report/${session.originalReportId}`;
  }
  
  return `/report/${session.originalReportId}`;
}